import memoizeOne from 'memoize-one';
import { token } from '@atlaskit/tokens';
import { branchDetailsEntryPoint as branchDetailsEntryPointOld } from '@atlassian/jira-development-board-branch-details-popup/entrypoint.tsx';
import { branchDetailsEntryPoint } from '@atlassian/jira-development-board-branch-details-popup-new/entrypoint.tsx';
import { commitDetailsEntryPoint as commitDetailsEntryPointOld } from '@atlassian/jira-development-board-commit-details-popup/entrypoint.tsx';
import { commitDetailsEntryPoint } from '@atlassian/jira-development-board-commit-details-popup-new/entrypoint.tsx';
import {
	ButtonStatus,
	DevInfoTypes,
	type ButtonStatusType,
	type DevInfoType,
} from '@atlassian/jira-development-board-common/src/types.tsx';
import { deploymentDetailsEntryPoint as deploymentDetailsEntryPointOld } from '@atlassian/jira-development-board-deployment-details-popup/entrypoint.tsx';
import { deploymentDetailsEntryPoint } from '@atlassian/jira-development-board-deployment-details-popup-new/entrypoint.tsx';
import { designDetailsEntryPoint } from '@atlassian/jira-development-board-design-details-popup/entrypoint.tsx';
import { pullRequestDetailsEntryPoint as pullRequestDetailsEntryPointOld } from '@atlassian/jira-development-board-pr-details-popup/entrypoint.tsx';
import { pullRequestDetailsNewEntryPoint as pullRequestDetailsEntryPoint } from '@atlassian/jira-development-board-pr-details-popup-new/entrypoint.tsx';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import messages from './messages.tsx';
import {
	branchDetailsPopupLoad,
	commitDetailsPopupLoad,
	deploymentDetailsPopupLoad,
	designDetailsPopupLoad,
	prDetailsPopupLoad,
} from './utils/performance-analytics/index.tsx';

type IconColor = Record<DevInfoType, Record<ButtonStatusType, string>>;

export const HOVER_TIMEOUT_MS = 300;
export const DevInfoButtonLabelMapping = memoizeOne((formatMessage: FormatMessage) => ({
	[DevInfoTypes.BRANCH]: formatMessage(messages.branchIconButtonLabel),
	[DevInfoTypes.COMMIT]: formatMessage(messages.commitIconButtonLabel),
	[DevInfoTypes.PULLREQUEST]: formatMessage(messages.pullRequestIconButtonLabel),
	[DevInfoTypes.MERGED]: formatMessage(messages.pullRequestIconButtonLabel),
	[DevInfoTypes.DECLINED]: formatMessage(messages.pullRequestIconButtonLabel),
	[DevInfoTypes.DEPLOYMENT]: formatMessage(messages.deploymentIconButtonLabel),
	[DevInfoTypes.DESIGN]: formatMessage(messages.designIconButtonLabel),
}));

export const iconColor: IconColor = {
	[DevInfoTypes.BRANCH]: {
		[ButtonStatus.ACTIVE]: token('color.icon.inverse'),
		[ButtonStatus.HOVER]: token('color.icon.subtle'),
		[ButtonStatus.DEFAULT]: token('color.icon.subtle'),
	},
	[DevInfoTypes.COMMIT]: {
		[ButtonStatus.ACTIVE]: token('color.icon.inverse'),
		[ButtonStatus.HOVER]: token('color.icon.subtle'),
		[ButtonStatus.DEFAULT]: token('color.icon.subtle'),
	},
	[DevInfoTypes.PULLREQUEST]: {
		[ButtonStatus.ACTIVE]: token('color.icon.inverse'),
		[ButtonStatus.HOVER]: token('color.icon.subtle'),
		[ButtonStatus.DEFAULT]: token('color.icon.subtle'),
	},
	[DevInfoTypes.MERGED]: {
		[ButtonStatus.ACTIVE]: token('color.icon.success'),
		[ButtonStatus.HOVER]: token('color.icon.success'),
		[ButtonStatus.DEFAULT]: token('color.icon.success'),
	},
	[DevInfoTypes.DECLINED]: {
		[ButtonStatus.ACTIVE]: token('color.icon.danger'),
		[ButtonStatus.HOVER]: token('color.icon.danger'),
		[ButtonStatus.DEFAULT]: token('color.icon.danger'),
	},
	[DevInfoTypes.DEPLOYMENT]: {
		[ButtonStatus.ACTIVE]: token('color.icon.inverse'),
		[ButtonStatus.HOVER]: token('color.icon.subtle'),
		[ButtonStatus.DEFAULT]: token('color.icon.subtle'),
	},
	[DevInfoTypes.DESIGN]: {
		[ButtonStatus.ACTIVE]: token('color.icon.inverse'),
		[ButtonStatus.HOVER]: token('color.icon.subtle'),
		[ButtonStatus.DEFAULT]: token('color.icon.subtle'),
	},
};

export const experienceDetails = {
	[DevInfoTypes.BRANCH]: {
		id: 'branchDetailsPopup',
		name: 'jiraSoftwareBoardBranchDetailsPopup',
	},
	[DevInfoTypes.COMMIT]: {
		id: 'commitDetailsPopup',
		name: 'jiraSoftwareBoardCommitDetailsPopup',
	},
	[DevInfoTypes.DEPLOYMENT]: {
		id: 'deploymentDetailsPopup',
		name: 'jiraSoftwareBoardDeploymentDetailsPopup',
	},
	[DevInfoTypes.PULLREQUEST]: { id: 'prDetailsPopup', name: 'jiraSoftwareBoardPrDetailsPopup' },
	[DevInfoTypes.DESIGN]: {
		id: 'designDetailsPopup',
		name: 'jiraSoftwareBoardDesignDetailsPopup',
	},
};

export const metrics = {
	[DevInfoTypes.BRANCH]: branchDetailsPopupLoad,
	[DevInfoTypes.COMMIT]: commitDetailsPopupLoad,
	[DevInfoTypes.DEPLOYMENT]: deploymentDetailsPopupLoad,
	[DevInfoTypes.PULLREQUEST]: prDetailsPopupLoad,
	[DevInfoTypes.DESIGN]: designDetailsPopupLoad,
};

export const packageDetails = {
	[DevInfoTypes.BRANCH]: {
		id: 'renderBranchDetailsPopup',
		packageName: 'jiraDevelopmentBoardBranchDetailsPopup',
		teamName: 'nova',
	},
	[DevInfoTypes.COMMIT]: {
		id: 'renderCommitDetailsPopup',
		packageName: 'jiraDevelopmentBoardCommitDetailsPopup',
		teamName: 'nova',
	},
	[DevInfoTypes.DEPLOYMENT]: {
		id: 'renderDeploymentDetailsPopup',
		packageName: 'jiraDevelopmentBoardDeploymentDetailsPopup',
		teamName: 'nova',
	},
	[DevInfoTypes.PULLREQUEST]: {
		id: 'renderPRDetailsPopup',
		packageName: 'jiraDevelopmentBoardPrDetailsPopup',
		teamName: 'nova',
	},
	[DevInfoTypes.DESIGN]: {
		id: 'renderDesignDetailsPopup',
		packageName: 'jiraDevelopmentBoardDesignDetailsPopup',
		teamName: 'helios-headcoach',
	},
};

export const popupEntryPointsOld = {
	[DevInfoTypes.BRANCH]: branchDetailsEntryPointOld,
	[DevInfoTypes.COMMIT]: commitDetailsEntryPointOld,
	[DevInfoTypes.DEPLOYMENT]: deploymentDetailsEntryPointOld,
	[DevInfoTypes.PULLREQUEST]: pullRequestDetailsEntryPointOld,
	[DevInfoTypes.DESIGN]: designDetailsEntryPoint,
};

export const popupEntryPoints = {
	[DevInfoTypes.BRANCH]: branchDetailsEntryPoint,
	[DevInfoTypes.COMMIT]: commitDetailsEntryPoint,
	[DevInfoTypes.DEPLOYMENT]: deploymentDetailsEntryPoint,
	[DevInfoTypes.PULLREQUEST]: pullRequestDetailsEntryPoint,
	[DevInfoTypes.DESIGN]: designDetailsEntryPoint,
};

export const DeploymentState = Object.freeze({
	SUCCESSFUL: 'SUCCESSFUL',
	FAILED: 'FAILED',
});

export const DeploymentEnvironment = Object.freeze({
	PRODUCTION: 'PRODUCTION',
	STAGING: 'STAGING',
	TESTING: 'TESTING',
});
